<template>
  <div class="offline-ride-container rtl-text">
    <div class="offline-indicator">{{ t('offlineRecoveryNotice') }}</div>
    <div v-if="isCheckingConnectivity" class="connectivity-check-indicator">
      🔍 {{ t('offlineCheckingConnectivity') }}
    </div>

    <h1 class="ride-title">{{ ride?.ride?.title }}</h1>
    <p v-if="ride?.ride?.text" class="ride-summary" v-html="ride?.ride?.text"></p>

    <div class="tab-container" v-if="ride?.attractionsByDay?.length">
      <div class="tab-buttons">
        <button
          v-for="(day, index) in ride.attractionsByDay"
          :key="index"
          :class="['tab-button', { active: activeDayIndex === index }]"
          @click="activeDayIndex = index"
        >
          {{ t('offlineDayLabel') }} {{ index + 1 }}
        </button>
      </div>

      <div class="ride-day-container" v-if="ride.attractionsByDay[activeDayIndex]">
        <h2 class="ride-day-title">
          {{ t('offlineDayLabel') }} {{ activeDayIndex + 1 }} - {{ ride.attractionsByDay[activeDayIndex].dateTime?.split('00:00')[0] }}
        </h2>

        <div v-if="ride.attractionsByDay[activeDayIndex].daily_attractions?.length">
          <h3 class="activity-heading">{{ t('offlineRecoveryActivitiesLabel') }}</h3>
          <div class="activity-list">
            <div
              v-for="(attraction, aIndex) in ride.attractionsByDay[activeDayIndex].daily_attractions"
              :key="aIndex"
              class="activity-item"
            >
              <p class="activity-title">{{ attraction.title }}</p>
              <p v-if="attraction.text" class="activity-text" v-html="attraction.text"></p>
              <button 
                v-if="attraction.latitude && attraction.longtitude"
                class="map-button"
                @click="openGoogleMapsLink(attraction)"
              >
                {{ t('offlineNavigateGoogleMaps') }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="ride.attractionsByDay[activeDayIndex].accommodation" class="accommodation-container">
          <h3 class="accommodation-heading">{{ t('offlineRecoveryAccommodationLabel') }}</h3>
          <p class="accommodation-name">{{ ride.attractionsByDay[activeDayIndex].accommodation.name }}</p>
          <p v-if="ride.attractionsByDay[activeDayIndex].accommodation.cuntry_and_city" class="accommodation-location">{{ ride.attractionsByDay[activeDayIndex].accommodation.cuntry_and_city }}</p>
          <p v-if="ride.attractionsByDay[activeDayIndex].accommodation.text" class="accommodation-text" v-html="ride.attractionsByDay[activeDayIndex].accommodation.text"></p>
          <button 
            v-if="ride.attractionsByDay[activeDayIndex].accommodation.latitude && ride.attractionsByDay[activeDayIndex].accommodation.longtitude"
            class="map-button"
            @click="openGoogleMapsLink(ride.attractionsByDay[activeDayIndex].accommodation)"
          >
            {{ t('offlineNavigateGoogleMaps') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="ride?.ride?.rideExtraInformation?.length">
      <div 
        class="extra-info-section"
        v-for="(rideExtraInformation, index) in ride.ride.rideExtraInformation"
        :key="index"
      >
        <h3 class="extra-info-title">{{ rideExtraInformation.title }}:</h3>
        <p class="extra-info-text" v-html="rideExtraInformation.text"></p>
      </div>
    </div>
  </div>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";

export default {
  name: 'OfflineRideView',
  mixins: [translationMixin],
  REDIRECT_GUARD_KEY: 'offlineRideRedirectGuardTs',
  REDIRECT_GUARD_MS: 15000,
  data() {
    return {
      ride: null,
      activeDayIndex: 0,
      // Store bound method references for cleanup
      boundCheckConnectivity: null,
      lastConnectivityCheck: 0,
      isCheckingConnectivity: false,
    }
  },
  mounted() {
    const cached = localStorage.getItem('lastRideData');
    if (cached) {
      try {
        this.ride = JSON.parse(cached);
        console.log(this.ride)
      } catch (e) {
        console.error('Failed to parse cached ride data', e);
      }
    }
    window.addEventListener('online', this.handleOnline);
    this.checkConnectivityOnLoad();
    
    // Add user interaction listeners to check connectivity
    this.addInteractionListeners();
    // window.addEventListener('offline', this.handleOffline);
  },
  
  beforeDestroy() {
    window.removeEventListener('online', this.handleOnline);
    this.removeInteractionListeners();
    // window.removeEventListener('offline', this.handleOffline);
  },
  
  methods: {
    canAttemptRedirect() {
      try {
        const previousAttemptTs = parseInt(sessionStorage.getItem(this.$options.REDIRECT_GUARD_KEY), 10);
        if (!Number.isNaN(previousAttemptTs) && (Date.now() - previousAttemptTs) < this.$options.REDIRECT_GUARD_MS) {
          return false;
        }
      } catch (e) {
        // Ignore storage access errors and continue.
      }
      return true;
    },
    markRedirectAttempt() {
      try {
        sessionStorage.setItem(this.$options.REDIRECT_GUARD_KEY, String(Date.now()));
      } catch (e) {
        // Ignore storage access errors.
      }
    },
    checkConnectivityOnLoad() {
      // Refresh on offline page should immediately verify connectivity and return to ride.
      if (!navigator.onLine) {
        return;
      }
      if (!this.canAttemptRedirect()) {
        return;
      }

      this.verifyConnectivity();
    },
    openGoogleMapsLink(place) {
      const lat = place.latitude;
      const lng = place.longtitude;
      console.log("the points are :",lat,", " ,lng)
      if (lat && lng) {
        // Try the geo: URI first (works on most mobile devices)
        const geoLink = `geo:${lat},${lng}`;
        const mapLink = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
        
        // Create a hidden link element
        const link = document.createElement('a');
        link.href = geoLink;
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Try to open with geo: URI
        link.click();
        
        // Remove the link element
        document.body.removeChild(link);
        
        // After a short delay, check if we need to fallback to Google Maps
        setTimeout(() => {
          window.open(mapLink, '_blank');
        }, 500);
      }
    },
    handleOnline() {
      if (!this.canAttemptRedirect()) {
        return;
      }

      // Add a small delay to ensure network is actually available (mobile browsers)
      setTimeout(() => {
        if (!navigator.onLine) {
          return;
        }
        
        const rideId = this.ride?.ride?.id || localStorage.getItem('lastRideId');
        if (rideId) {
          // Save scroll position before redirecting back to main page
          const savedPosition = localStorage.getItem('scrollPositionBeforeOffline');
          if (savedPosition !== null) {
            localStorage.setItem('scrollPositionToRestore', savedPosition);
            localStorage.removeItem('scrollPositionBeforeOffline');
          }
          this.markRedirectAttempt();
          // Use replace to avoid adding to history stack
          window.location.replace(`/single_ride_with_map/${rideId}`);
        }
      }, 500);
    },
    
    addInteractionListeners() {
      // Bind the method once to avoid creating new functions on each call
      this.boundCheckConnectivity = this.checkConnectivityOnInteraction.bind(this);
      
      // Listen for various user interactions
      document.addEventListener('click', this.boundCheckConnectivity);
      document.addEventListener('scroll', this.boundCheckConnectivity);
      document.addEventListener('touchstart', this.boundCheckConnectivity);
      document.addEventListener('keydown', this.boundCheckConnectivity);
      document.addEventListener('focus', this.boundCheckConnectivity, true);
    },
    
    removeInteractionListeners() {
      if (this.boundCheckConnectivity) {
        document.removeEventListener('click', this.boundCheckConnectivity);
        document.removeEventListener('scroll', this.boundCheckConnectivity);
        document.removeEventListener('touchstart', this.boundCheckConnectivity);
        document.removeEventListener('keydown', this.boundCheckConnectivity);
        document.removeEventListener('focus', this.boundCheckConnectivity, true);
      }
    },
    
    checkConnectivityOnInteraction() {
      // Throttle checks to avoid excessive calls
      if (this.lastConnectivityCheck && Date.now() - this.lastConnectivityCheck < 1000) {
        return;
      }
      this.lastConnectivityCheck = Date.now();
      
      // Check if browser thinks we're online
      if (navigator.onLine) {
        // Additional verification: try to fetch a small resource
        this.verifyConnectivity();
      }
    },
    
    async verifyConnectivity() {
      // Show checking indicator
      this.isCheckingConnectivity = true;
      
      try {
        // Try to fetch a small resource to verify real connectivity
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
        
        const response = await fetch('/favicon.ico?' + Date.now(), {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          console.log('✅ Internet connectivity verified! Returning to online mode...');
          this.handleOnline();
        } else {
          console.log('❌ Fetch failed, still offline');
        }
      } catch (error) {
        console.log('❌ Connectivity verification failed:', error.message);
        // Could be timeout, network error, or abort - still offline
      } finally {
        // Hide checking indicator
        this.isCheckingConnectivity = false;
      }
    },
  }
}
</script>

<style scoped>
.offline-ride-container {
  max-width: 800px;
  margin: 0 auto;
  direction: rtl;
  background-color: #f9fafb;
  padding: 16px;
}

.rtl-text {
  text-align: right;
}

.ride-title {
  font-size: 26px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 16px;
  color: #111;
}

.ride-summary {
  text-align: center;
  margin-bottom: 24px;
  font-size: 16px;
  color: #444;
  white-space: pre-wrap;
}

.tab-container {
  margin-bottom: 24px;
}

.tab-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  justify-content: center;
}

.tab-button {
  background-color: #e2e8f0;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  cursor: pointer;
}

.tab-button.active {
  background-color: #3b82f6;
  color: white;
}

.ride-day-container {
  padding: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ride-day-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
  color: #222;
}

.activity-heading {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
}

.activity-list {
  padding: 0;
}

.activity-item {
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
}

.activity-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #111;
}

.activity-text {
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.map-button {
  font-size: 14px;
  color: #1a73e8;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
}

.accommodation-container {
  margin-top: 24px;
  background-color: #f1f5f9;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 12px;
}

.accommodation-heading {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.accommodation-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #111;
}

.accommodation-location {
  margin-bottom: 4px;
  font-size: 14px;
  color: #555;
}

.accommodation-text {
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  white-space: pre-wrap;
}

.extra-info-section {
  margin-top: 32px;
  background-color: #fff;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.extra-info-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #222;
}

.extra-info-text {
  font-size: 14px;
  color: #444;
  line-height: 1.6;
  white-space: pre-wrap;
}
.offline-indicator {
  background-color: #fde047;
  color: #92400e;
  padding: 12px;
  text-align: center;
  font-weight: bold;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.connectivity-check-indicator {
  background-color: #dbeafe;
  color: #1d4ed8;
  padding: 8px 12px;
  text-align: center;
  font-weight: 500;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 13px;
  border: 1px solid #93c5fd;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
